import React, { useState, useEffect } from "react";
import { logAction } from "../utils/logsApi";

const SESSION_ID = "wqaha9g53kl7jgqifynhzw2w24c269mh";
const SESSION_ID_SIGN = "v3:H/WXSTs2lUusduToZfQe1PL17suxt6eJKv1pBz+s0Q0=";

const AVAILABLE_STRATEGIES = [
    {
        id: "semi-automated",
        name: "Semi Automated Trading",
        url: "https://www.tradingview.com/script/B4A8mFor-AdamoLabs-Semi-Automated-Trading/",
        pine_id: "PUB;942c5ab5c7bd41b3aca651797562cd9b"
    },
    {
        id: "camarilla-manual",
        name: "Camarilla Manual Trading",
        url: "https://www.tradingview.com/script/OM2zAVQY-AdamoLabs-Manual-Trading/",
        pine_id: "PUB;2101292373f84dd69920cc0803f06700"
    },
    {
        id: "macd-sniper",
        name: "MACD Sniper",
        url: "https://www.tradingview.com/script/ocuRWJ7B-AdamoLabs-MACD-Sniper/",
        pine_id: "PUB;1167db8fd8f3406da2526efa1913d54d"
    },
    {
        id: "semi-automated-continuations",
        name: "Semi Automated Trading - Continuations",
        url: "https://www.tradingview.com/script/BsBeGTs5-AdamoLabs-Semi-Automated-Trading-Continuations/",
        pine_id: "PUB;636c2dc864e34052b3573d97c3212acf"
    },
    {
        id: "Bandas-2",
        name: "Bandas 2.0",
        url: "https://www.tradingview.com/script/duPLJeQM-Bandas-2-0/",
        pine_id: "PUB;28a0f25e43a34935a2720f73188a763b"
    }
];

const TradingViewManager = () => {
    const [selectedStrategy, setSelectedStrategy] = useState("");
    const [username, setUsername] = useState("");
    const [action, setAction] = useState("add");
    const [responseMessage, setResponseMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Authentication check
    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            window.location.href = "/";
            return;
        }
    }, []);

    const resetForm = () => {
        setSelectedStrategy("");
        setUsername("");
    };

    const handleAction = async () => {
        setResponseMessage("");

        if (!selectedStrategy || selectedStrategy === "") {
            setResponseMessage("❌ Error: Please select a strategy from the dropdown");
            return;
        }

        const strategy = AVAILABLE_STRATEGIES.find(s => s.id === selectedStrategy);
        if (!strategy) {
            setResponseMessage("❌ Error: Invalid strategy selected. Please choose a valid strategy.");
            return;
        }

        if (!username || username.trim() === "") {
            setResponseMessage("❌ Error: Please enter a valid username");
            return;
        }

        const pineId = strategy.pine_id;
        if (!pineId) {
            setResponseMessage("❌ Error: Could not extract script ID from strategy URL");
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('pine_id', pineId);
            formData.append('username_recip', username.trim());
            formData.append('sessionid', SESSION_ID);
            formData.append('sessionid_sign', SESSION_ID_SIGN);
            formData.append('action', action);

            const response = await fetch('https://tradingview-proxy.ream-850.workers.dev/', {
                method: "POST",
                body: formData
            });

            if (response.status === 201) {
                const successMessage = action === "add" ? "Access granted successfully" : "Access removed successfully";
                setResponseMessage(`✅ Success: ${successMessage}`);
                
                await logAction(
                    action === "add" ? "ADD_TRADINGVIEW_ACCESS" : "REMOVE_TRADINGVIEW_ACCESS",
                    localStorage.getItem("userEmail"),
                    `Strategy: ${strategy.name}, User: ${username}`,
                    null,
                    "TradingView Access"
                );

                resetForm();
            } else if (response.status === 200) {
                const responseData = await response.json();
                
                if (responseData.status === "exists") {
                    setResponseMessage("⚠️ User already has access to this strategy.");
                } else {
                    const successMessage = action === "add" ? "Action completed successfully" : "Access removed successfully";
                    setResponseMessage(`✅ Success: ${successMessage}`);
                }
                
                await logAction(
                    action === "add" ? "ADD_TRADINGVIEW_ACCESS" : "REMOVE_TRADINGVIEW_ACCESS",
                    localStorage.getItem("userEmail"),
                    `Strategy: ${strategy.name}, User: ${username}, Status: ${responseData.status || 'unknown'}`,
                    null,
                    "TradingView Access"
                );

                resetForm();
            } else if (response.status === 422) {
                const responseData = await response.json();
                
                if (responseData.code === "username_recip_not_found") {
                    setResponseMessage(`❌ Error: ${responseData.detail}`);
                    
                    await logAction(
                        "TRADINGVIEW_ACCESS_ERROR",
                        localStorage.getItem("userEmail"),
                        responseData.detail,
                        null,
                        "TradingView Access"
                    );
                } else {
                    const errorMessage = `Failed to ${action === "add" ? "grant" : "remove"} access. Status: ${response.status}`;
                    setResponseMessage("❌ Error: " + errorMessage);
                    
                    await logAction(
                        "TRADINGVIEW_ACCESS_ERROR",
                        localStorage.getItem("userEmail"),
                        `Failed to ${action === "add" ? "add" : "remove"} access: ${errorMessage}`,
                        null,
                        "TradingView Access"
                    );
                }
            } else {
                const errorMessage = `Failed to ${action === "add" ? "grant" : "remove"} access. Status: ${response.status}`;
                setResponseMessage("❌ Error: " + errorMessage);
                
                await logAction(
                    "TRADINGVIEW_ACCESS_ERROR",
                    localStorage.getItem("userEmail"),
                    `Failed to ${action === "add" ? "add" : "remove"} access: ${errorMessage}`,
                    null,
                    "TradingView Access"
                );
            }
        } catch (error) {
            console.error("Error during API call:", error);
            setResponseMessage("❌ Error: An unexpected error occurred. Please try again later.");
            
            await logAction(
                "TRADINGVIEW_ACCESS_ERROR",
                localStorage.getItem("userEmail"),
                `Unexpected error: ${error.message}`,
                null,
                "TradingView Access"
            );
        }
        setIsLoading(false);
    };

    return (
        <div className="tradingview-container">
            <h2>TradingView Access Manager</h2>
            
            {responseMessage && (
                <div className={`response-message ${responseMessage.includes("Success") ? "success" : responseMessage.includes("⚠️") ? "warning" : "error"}`} 
                     style={{
                         padding: '15px',
                         margin: '20px 0',
                         borderRadius: '8px',
                         backgroundColor: responseMessage.includes("Success") ? '#d4edda' : responseMessage.includes("⚠️") ? '#fff3cd' : '#f8d7da',
                         color: responseMessage.includes("Success") ? '#155724' : responseMessage.includes("⚠️") ? '#856404' : '#721c24',
                         border: `1px solid ${responseMessage.includes("Success") ? '#c3e6cb' : responseMessage.includes("⚠️") ? '#ffeeba' : '#f5c6cb'}`,
                         fontSize: '16px',
                         fontWeight: 'bold',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center'
                     }}>
                    {responseMessage}
                </div>
            )}

            <div className="input-group">
                <label>Action</label>
                <select
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    className="filter-input"
                >
                    <option value="add">Grant Access</option>
                    <option value="remove">Remove Access</option>
                </select>
            </div>

            <div className="input-group">
                <label>Select Strategy</label>
                <select
                    value={selectedStrategy}
                    onChange={(e) => setSelectedStrategy(e.target.value)}
                    className="filter-input"
                >
                    <option value="">Select a strategy</option>
                    {AVAILABLE_STRATEGIES.map(strategy => (
                        <option key={strategy.id} value={strategy.id}>
                            {strategy.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="input-group">
                <label>Username</label>
                <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter Username"
                    className="filter-input"
                />
            </div>

            <div style={{ marginTop: '20px' }}>
                {isLoading ? (
                    <div style={{ color: '#666', marginBottom: '10px' }}>Processing your request...</div>
                ) : (
                    (!selectedStrategy || !username) && (
                        <div style={{ 
                            color: '#721c24', 
                            backgroundColor: '#f8d7da', 
                            padding: '10px', 
                            borderRadius: '4px',
                            marginBottom: '10px',
                            fontSize: '14px'
                        }}>
                            {!selectedStrategy && "⚠️ Please select a strategy from the dropdown above"}
                            {selectedStrategy && !username && "⚠️ Please enter a username"}
                        </div>
                    )
                )}
                <button 
                    onClick={handleAction} 
                    className="action-button" 
                    disabled={isLoading || !selectedStrategy || !username}
                    style={{
                        opacity: (isLoading || !selectedStrategy || !username) ? 0.6 : 1,
                        cursor: (isLoading || !selectedStrategy || !username) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isLoading ? "Processing..." : action === "add" ? "Grant Access" : "Remove Access"}
                </button>
            </div>
        </div>
    );
};

export default TradingViewManager;
